import { c as _c } from "react/compiler-runtime";
import { ClosedLetterIcon, OpenInNewIcon, OpenedLetterIcon, TapIcon } from '@systemeio/ui/icons';
import { DateRangePicker, Tooltip, getDate, getLast3MonthsCustomFilter } from '@systemeio/ui';
import { useContactEmails } from 'modules/contacts/contact-page/hooks/use-contact-emails';
import { ContactEmailsFilterType, ContactEmailsInterface } from 'modules/contacts/contact-page/types/contact-emails-interface';
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface';
import React, { useEffect } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { OrderEnum } from 'shared/types/pagination-interface';
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format';
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates';
interface ContactEmailsProps {
  contactId: ContactId;
  contact?: ContactInterface;
}
const defaultFilters: Required<Pick<ContactEmailsFilterType, 'date'>> = {
  date: [null, null]
};
const ContactEmails = t0 => {
  const $ = _c(86);
  const {
    contactId,
    contact
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters, true);
  const t1 = filter.date[0] !== null;
  let t2;
  if ($[0] !== contactId || $[1] !== filter || $[2] !== limit || $[3] !== pagination || $[4] !== t1) {
    t2 = {
      contactId,
      limit,
      pagination,
      filter,
      shouldFetch: t1
    };
    $[0] = contactId;
    $[1] = filter;
    $[2] = limit;
    $[3] = pagination;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    contactEmails
  } = useContactEmails(t2);
  let t3;
  if ($[6] !== contact || $[7] !== setFilter || $[8] !== user?.timezone) {
    t3 = () => {
      if (contact) {
        setFilter(prev => ({
          ...prev,
          date: getDateRangeSinceDate(contact.createdAt, user?.timezone)
        }));
      }
    };
    $[6] = contact;
    $[7] = setFilter;
    $[8] = user?.timezone;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = user?.timezone;
  let t5;
  if ($[10] !== contact || $[11] !== setFilter || $[12] !== t4) {
    t5 = [contact, setFilter, t4];
    $[10] = contact;
    $[11] = setFilter;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t3, t5);
  let t6;
  if ($[14] !== t) {
    t6 = t("dashboard.contact_page.subject");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t || $[17] !== user?.dateFormat || $[18] !== user?.timezone) {
    t7 = email => <div className="flex w-full gap-1.5">{email.openedAt !== null ? <Tooltip mode="hover" label={<div className="flex gap-1"><span className="text-text-primary">{t("dashboard.contact_page.email_opened_at")}</span><span className="text-text-primary">{dateFormat(email.openedAt, {
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone
          })}</span></div>}><OpenedLetterIcon className="h-[20px] w-[20px] shrink-0" /></Tooltip> : <ClosedLetterIcon className="h-[20px] w-[20px] shrink-0" />}<Tooltip wrapperClassName="w-full truncate" mode="overflow" label={email.email.subject}>{email.email.subject}</Tooltip>{email.clicked && <Tooltip wrapperClassName="flex w-fit justify-end" mode="hover" label={t("dashboard.contact_page.email_clicked_popover")}><TapIcon className="fill-brand-primary h-[20px] w-[20px] shrink-0" /></Tooltip>}</div>;
    $[16] = t;
    $[17] = user?.dateFormat;
    $[18] = user?.timezone;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = {
      key: "email",
      header: t6,
      widthPercent: 70,
      onRender: t7
    };
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t) {
    t9 = t("dashboard.contact_page.sent_at");
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== user?.dateFormat || $[26] !== user?.timeFormat || $[27] !== user?.timezone) {
    t10 = email_0 => <Tooltip wrapperClassName="w-full" mode="hover" label={dateTimeFormat(email_0.createdAt, {
      timeFormat: user?.timeFormat,
      dateFormat: user?.dateFormat,
      timeZone: user?.timezone
    })}><span className="w-full truncate">{dateFormat(email_0.createdAt, {
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })}</span></Tooltip>;
    $[25] = user?.dateFormat;
    $[26] = user?.timeFormat;
    $[27] = user?.timezone;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t || $[30] !== user?.dateFormat || $[31] !== user?.timeFormat || $[32] !== user?.timezone) {
    t11 = email_1 => <div className="flex items-center gap-3"><span className="text-text-primary font-bold">{t("dashboard.contact_page.sent_at")}</span><span>{dateTimeFormat(email_1.createdAt, {
          timeFormat: user?.timeFormat,
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })}</span></div>;
    $[29] = t;
    $[30] = user?.dateFormat;
    $[31] = user?.timeFormat;
    $[32] = user?.timezone;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t9) {
    t12 = {
      key: "createdAt",
      header: t9,
      widthPercent: 30,
      onRender: t10,
      onRenderSmall: t11
    };
    $[34] = t10;
    $[35] = t11;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t12 || $[39] !== t8) {
    t13 = [t8, t12];
    $[38] = t12;
    $[39] = t8;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const columns = t13;
  let t14;
  if ($[41] !== contact || $[42] !== t) {
    t14 = contact ? <LinkWithoutPrefetch className="text-text-primary hover:text-brand-primary focus-visible:text-brand-primary group text-[17px] outline-hidden transition-colors duration-200 ease-linear" href={`/contacts/${contact.id}/email-history`}><div className="flex items-center gap-1.5">{t("dashboard.contact_page.email_history")}<OpenInNewIcon className="fill-text-primary group-hover:fill-brand-primary transition-colors duration-200 ease-linear" /></div></LinkWithoutPrefetch> : <span className="text-[17px]">{t("dashboard.contact_page.email_history")}</span>;
    $[41] = contact;
    $[42] = t;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const t15 = user?.timezone;
  let t16;
  if ($[44] !== t15) {
    t16 = getToday(t15);
    $[44] = t15;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const t17 = contact?.createdAt;
  const t18 = user?.timezone;
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = getDate(t17, t18);
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== contact?.createdAt || $[50] !== user?.timezone) {
    t20 = () => getDateRangeSinceDate(contact?.createdAt, user?.timezone);
    $[49] = contact?.createdAt;
    $[50] = user?.timezone;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const t21 = filter.date;
  const t22 = user?.timezone;
  let t23;
  if ($[52] !== t22) {
    t23 = getLast3MonthsCustomFilter(t22);
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t23) {
    t24 = [t23];
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== setFilter || $[57] !== setLimit || $[58] !== setPagination) {
    t25 = date => {
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      setLimit(10);
      setFilter(prev_0 => ({
        ...prev_0,
        date
      }));
    };
    $[56] = setFilter;
    $[57] = setLimit;
    $[58] = setPagination;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  const t26 = user?.timezone;
  const t27 = user?.createdAt;
  let t28;
  if ($[60] !== filter.date || $[61] !== intlLocale || $[62] !== t16 || $[63] !== t19 || $[64] !== t20 || $[65] !== t24 || $[66] !== t25 || $[67] !== t26 || $[68] !== t27) {
    t28 = <DateRangePicker maxDate={t16} minDate={t19} getDefaultDate={t20} date={t21} customFilters={t24} onChange={t25} timezone={t26} createdAt={t27} intlLocale={intlLocale} />;
    $[60] = filter.date;
    $[61] = intlLocale;
    $[62] = t16;
    $[63] = t19;
    $[64] = t20;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== limit || $[71] !== setLimit) {
    t29 = {
      limit,
      setLimit
    };
    $[70] = limit;
    $[71] = setLimit;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== pagination || $[74] !== setPagination) {
    t30 = {
      pagination,
      setPagination
    };
    $[73] = pagination;
    $[74] = setPagination;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== columns || $[77] !== contactEmails || $[78] !== t29 || $[79] !== t30) {
    t31 = <Table limiter={t29} paginator={t30} data={contactEmails} columns={columns} />;
    $[76] = columns;
    $[77] = contactEmails;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== contactEmails || $[82] !== t14 || $[83] !== t28 || $[84] !== t31) {
    t32 = <ListingLayout counter={false} data={contactEmails} type="default" title={t14} customHeaderElement={t28}>{t31}</ListingLayout>;
    $[81] = contactEmails;
    $[82] = t14;
    $[83] = t28;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  return t32;
};
export default ContactEmails;